/**
 * The one list. Every page's rows are built from the same parts so they share padding, gaps, alignment and overflow:
 *
 *   <List>
 *     <ListRow href={…}>                      a row; with href the whole row opens it (its title is the link)
 *       <RowLead><Face … /></RowLead>         face, icon, logo or date: never shrinks
 *       <RowMain>                              takes the rest; never pushes the columns off the row
 *         <RowTitle>Name</RowTitle>            one line, cut with … (lines={2} for a two-line clamp)
 *         <RowSub>Ward 32 · Alderperson</RowSub>
 *       </RowMain>
 *       <RowCell width={150}>…</RowCell>       a fixed column (status, numbers, a meter); hide="phone" drops it on phones
 *       <RowTrail>chip, count, time</RowTrail>
 *       <RowActions>buttons</RowActions>       sits above the row's link, so its buttons still work
 *     </ListRow>
 *   </List>
 *
 * The page's own class (className) adds its look (sizes, colors); the layout and the overflow rules are these.
 * When the row is too narrow the columns move to a second line instead of overlapping (components.css, "List").
 */
import { createContext, useContext, type CSSProperties, type ElementType, type HTMLAttributes, type MouseEvent, type Ref, type ReactNode } from "react";

/** role, aria-*, id, tabIndex, event handlers and ref: passed through to the element. */
type Passthrough = Omit<HTMLAttributes<HTMLElement>, "children" | "className" | "style" | "title"> & { ref?: Ref<HTMLElement> };
import { navigate, plainClick } from "../../app/router";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

type Variant = "divided" | "plain" | "cards";

export function List({ children, variant = "divided", dense, className, as: As = "ul", label, ...rest }: {
  children: ReactNode; variant?: Variant; dense?: boolean; className?: string; as?: ElementType; label?: string;
} & Passthrough & Record<`data-${string}`, string | boolean | undefined>) {
  return (
    <As className={cx("ls", `ls--${variant}`, dense && "ls--dense", className)} aria-label={label} {...rest}>{children}</As>
  );
}

const RowLink = createContext<{ href?: string; onOpen?: () => void } | null>(null);

export function ListRow({ children, className, as: As = "li", href, onOpen, current, selected, style, wrap = true, ...rest }: {
  children: ReactNode; className?: string; as?: ElementType;
  /** The row opens this page (soft navigation). Its RowTitle becomes the link and covers the row. */
  href?: string;
  /** The row does this when clicked (a preview, a selection). Its RowTitle becomes the button and covers the row.
   *  With href too, the title is a link: a plain click does this, a new-tab click opens href. */
  onOpen?: () => void;
  current?: boolean; selected?: boolean; style?: CSSProperties;
  /** false: never move columns to a second line (rows whose columns already fit, e.g. a name and a switch). */
  wrap?: boolean;
} & Passthrough & Record<`data-${string}`, string | number | boolean | undefined>) {
  const hit = href != null || onOpen != null;
  return (
    <As className={cx("ls-row", hit && "ls-row--hit", !wrap && "ls-row--nowrap", className)} style={style}
      aria-current={current || undefined} data-selected={selected || undefined} {...rest}>
      <RowLink.Provider value={hit ? { href, onOpen } : null}>{children}</RowLink.Provider>
    </As>
  );
}

/** Face, icon, logo or date at the row's start. */
export const RowLead = ({ children, className, size }: { children: ReactNode; className?: string; size?: number }) =>
  <span className={cx("ls-lead", className)} style={size ? { width: size } : undefined}>{children}</span>;

/** The row's text: takes the space left after the columns, and never more. */
export const RowMain = ({ children, className, min }: { children: ReactNode; className?: string; min?: number }) =>
  <span className={cx("ls-main", className)} style={min ? ({ "--ls-main-min": `${min}px` } as CSSProperties) : undefined}>{children}</span>;

/** The row's name. With the row's href/onOpen it is the link (covering the row); one line unless lines is set. */
export function RowTitle({ children, className, lines, as: As = "span", title }: {
  children: ReactNode; className?: string; lines?: 1 | 2 | 3; as?: ElementType; title?: string;
}) {
  const link = useContext(RowLink);
  const cls = cx("ls-title", lines && lines > 1 && "ls-clamp", className);
  const style = lines && lines > 1 ? ({ "--ls-lines": lines } as CSSProperties) : undefined;
  if (link?.href != null) {
    const href = link.href;
    const open = link.onOpen;   // both: a plain click does onOpen (a preview), a new-tab click opens href
    const go = (e: MouseEvent) => { if (!plainClick(e)) return; e.preventDefault(); if (open) open(); else navigate(href); };
    return <As className={cls} style={style}><a className="ls-hit" href={href} onClick={go} title={title}>{children}</a></As>;
  }
  if (link?.onOpen) {
    const open = link.onOpen;
    return <As className={cls} style={style}><button type="button" className="ls-hit" onClick={open} title={title}>{children}</button></As>;
  }
  return <As className={cls} style={style} title={title}>{children}</As>;
}

/** A line under the title (role, ward, record number, date). One line, cut with …; wrap lets it wrap instead. */
export const RowSub = ({ children, className, wrap }: { children: ReactNode; className?: string; wrap?: boolean }) =>
  <span className={cx("ls-sub", wrap && "ls-sub--wrap", className)}>{children}</span>;

/** A row of small things under the title (chips, tags): wraps onto more lines rather than overflow. */
export const RowMeta = ({ children, className }: { children: ReactNode; className?: string }) =>
  <span className={cx("ls-meta", className)}>{children}</span>;

/** A fixed-width column. hide="phone" drops it under 768 px; align="end" for numbers. */
export const RowCell = ({ children, className, width, align, hide, label }: {
  children?: ReactNode; className?: string; width?: number | string; align?: "start" | "center" | "end"; hide?: "phone"; label?: string;
}) => (
  <span className={cx("ls-cell", align && `ls-cell--${align}`, hide && `ls-hide-${hide}`, className)} aria-label={label}
    style={width != null ? ({ "--ls-w": typeof width === "number" ? `${width}px` : width } as CSSProperties) : undefined}>{children}</span>
);

/** Things at the row's end that don't need a column (a chip, a count, a time, a chevron). */
export const RowTrail = ({ children, className }: { children: ReactNode; className?: string }) =>
  <span className={cx("ls-trail", className)}>{children}</span>;

/** Buttons and menus at the row's end. They sit above the row's link. */
export const RowActions = ({ children, className }: { children: ReactNode; className?: string }) =>
  <span className={cx("ls-actions", className)} onClick={(e) => e.stopPropagation()}>{children}</span>;

/** A full-width part under the row's main line (a quote, a bar, a note). */
export const RowBelow = ({ children, className }: { children: ReactNode; className?: string }) =>
  <span className={cx("ls-below", className)}>{children}</span>;
